/**
 * The VDP workspace design system.
 *
 * One source of truth for the surfaces, controls and data displays every page is
 * built from — flat rounded panels, accent-rail headings, pill controls, chip
 * tables — so the customer area, the admin console and the auth screens read as
 * one product instead of a dozen hand-styled pages.
 *
 * Both themes are first-class: every token carries its light and dark palette,
 * driven by the `dark` class the Header's toggle puts on <html>.
 *
 * Usage:
 *   import { Panel, PageHeader, Button, Table, Th, Td } from "../components/ui";
 */

import React from "react";
import { Link } from "react-router-dom";

/* -------------------------------------------------------------------------- */
/* Tokens                                                                      */
/* -------------------------------------------------------------------------- */

/** A workspace surface: the large rounded cards pages are built from. */
export const PANEL =
  "rounded-[22px] border border-gray-200 bg-white shadow-lg shadow-gray-900/5 dark:border-[#2c2c2e] dark:bg-[#1c1c1e] dark:shadow-black/40";
/** A recessed surface inside a panel (table shells, empty states, value tiles). */
export const SUNKEN = "border-gray-200 bg-gray-50 dark:border-[#2c2c2e] dark:bg-[#161617]";
/** Inputs and secondary (pill) controls. */
export const CONTROL =
  "border-gray-300 bg-white text-gray-800 placeholder-gray-400 dark:border-[#3a3a3d] dark:bg-[#262628] dark:text-slate-100 dark:placeholder-slate-500";
/** The focus treatment every field shares. */
export const FOCUS = "outline-none transition focus:border-red-400 focus:ring-4 focus:ring-red-500/10";
/** Brand gradient, for fills and for text via bg-clip. */
export const ACCENT_GRADIENT = "bg-gradient-to-r from-red-600 to-orange-500";
export const ACCENT_TEXT = "bg-gradient-to-r from-red-600 to-orange-500 bg-clip-text text-transparent";
/** The small vertical bar that marks a heading. */
export const ACCENT_RAIL = "rounded-full bg-gradient-to-b from-red-500 to-orange-400";

const BUTTON_BASE =
  "inline-flex items-center justify-center gap-2 rounded-full font-bold transition disabled:cursor-not-allowed disabled:opacity-60";

const BUTTON_SIZES = {
  sm: "h-9 px-4 text-xs",
  md: "h-11 px-6 text-sm",
  lg: "h-12 px-8 text-base",
};

const BUTTON_VARIANTS = {
  primary: `${ACCENT_GRADIENT} text-white shadow-lg shadow-red-500/25 hover:from-red-500 hover:to-orange-400 disabled:bg-none disabled:bg-gray-200 disabled:text-gray-400 disabled:shadow-none dark:disabled:bg-[#2a2a2c] dark:disabled:text-slate-500`,
  ghost: `border font-semibold hover:border-red-400/60 ${CONTROL}`,
  danger: "bg-rose-500 text-white hover:bg-rose-400",
  success: "bg-emerald-500 text-white hover:bg-emerald-400",
  subtle:
    "bg-gray-100 font-semibold text-gray-600 hover:bg-gray-200 dark:bg-[#262628] dark:text-slate-300 dark:hover:bg-[#303032]",
};

/**
 * The button's classes without the component — for a native <button> that needs
 * its own type, form wiring or markup, and for links styled as buttons.
 */
export const buttonClass = ({ variant = "primary", size = "md", className = "" } = {}) =>
  `${BUTTON_BASE} ${BUTTON_SIZES[size]} ${BUTTON_VARIANTS[variant]} ${className}`;

/* -------------------------------------------------------------------------- */
/* Layout                                                                      */
/* -------------------------------------------------------------------------- */

/** The page frame: centred, with the app layout's own padding already applied. */
export function Page({ children, className = "", width = "max-w-[1500px]", ...rest }) {
  return (
    <div className={`mx-auto ${width} space-y-5 px-4 pb-4 sm:px-6 sm:pb-6 ${className}`} {...rest}>
      {children}
    </div>
  );
}

/** Page title with the accent rail, an optional back arrow, and right-aligned actions. */
export function PageHeader({ title, subtitle, onBack, children }) {
  return (
    <div className="flex flex-wrap items-center gap-3">
      <div className="flex min-w-0 items-center gap-3">
        {onBack ? (
          <button
            type="button"
            onClick={onBack}
            aria-label="Go back"
            className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border transition hover:border-red-400/60 hover:text-red-500 ${CONTROL}`}
          >
            <StrokeIcon className="h-4 w-4">
              <path d="M15 6l-6 6 6 6" />
            </StrokeIcon>
          </button>
        ) : null}
        <div className="min-w-0">
          <h1 className="flex items-center gap-2.5 text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
            <span className={`h-6 w-1.5 ${ACCENT_RAIL}`} />
            <span className="truncate">{title}</span>
          </h1>
          {subtitle ? (
            <p className="mt-1 pl-4 text-sm text-gray-500 dark:text-slate-400">{subtitle}</p>
          ) : null}
        </div>
      </div>
      {children ? <div className="ml-auto flex flex-wrap items-center gap-3">{children}</div> : null}
    </div>
  );
}

export function Panel({ children, className = "", padded = true, ...rest }) {
  return (
    <section className={`${PANEL} ${padded ? "p-5 sm:p-6" : ""} ${className}`} {...rest}>
      {children}
    </section>
  );
}

/** A heading inside a panel, with an optional badge and right-aligned controls. */
export function SectionHeading({ title, badge, children }) {
  return (
    <div className="flex flex-wrap items-center gap-3">
      <h2 className="flex items-center gap-2.5 text-xl font-semibold tracking-tight text-gray-900 dark:text-white">
        <span className={`h-5 w-1 ${ACCENT_RAIL}`} />
        {title}
      </h2>
      {badge}
      {children ? <div className="ml-auto flex flex-wrap items-center gap-2">{children}</div> : null}
    </div>
  );
}

export function GroupLabel({ children, className = "" }) {
  return (
    <p
      className={`text-[10px] font-bold uppercase tracking-[0.14em] text-gray-400 dark:text-slate-500 ${className}`}
    >
      {children}
    </p>
  );
}

export function Divider({ className = "" }) {
  return <div className={`h-px bg-gray-200 dark:bg-[#2c2c2e] ${className}`} />;
}

/* -------------------------------------------------------------------------- */
/* Controls                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * The app's button. Renders a react-router <Link> when given `to`, an <a> when
 * given `href`, and a <button> otherwise, so every call site gets the same shape.
 */
export function Button({
  variant = "primary",
  size = "md",
  className = "",
  to,
  href,
  children,
  ...rest
}) {
  const classes = buttonClass({ variant, size, className });
  if (to) {
    return (
      <Link to={to} className={classes} {...rest}>
        {children}
      </Link>
    );
  }
  if (href) {
    return (
      <a href={href} className={classes} {...rest}>
        {children}
      </a>
    );
  }
  return (
    <button type="button" className={classes} {...rest}>
      {children}
    </button>
  );
}

/** A round icon-only control — table row actions, toolbar buttons, modal closers. */
export function IconButton({ className = "", title, children, to, ...rest }) {
  const classes = `inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border transition hover:border-red-400/60 hover:text-red-500 disabled:cursor-not-allowed disabled:opacity-50 ${CONTROL} ${className}`;
  if (to) {
    return (
      <Link to={to} title={title} aria-label={title} className={classes} {...rest}>
        {children}
      </Link>
    );
  }
  return (
    <button type="button" title={title} aria-label={title} className={classes} {...rest}>
      {children}
    </button>
  );
}

export function Input({ className = "", ...rest }) {
  return (
    <input className={`h-11 w-full rounded-[12px] border px-4 text-sm ${CONTROL} ${FOCUS} ${className}`} {...rest} />
  );
}

export function Select({ className = "", children, ...rest }) {
  return (
    <select className={`h-11 w-full rounded-[12px] border px-3 text-sm ${CONTROL} ${FOCUS} ${className}`} {...rest}>
      {children}
    </select>
  );
}

export function Textarea({ className = "", ...rest }) {
  return (
    <textarea
      className={`w-full rounded-[12px] border px-4 py-3 text-sm ${CONTROL} ${FOCUS} ${className}`}
      {...rest}
    />
  );
}

/** A labelled form row: label, control, and an optional hint or error beneath. */
export function Field({ label, hint, error, required, className = "", children }) {
  return (
    <div className={className}>
      {label ? (
        <label className="mb-1.5 block text-xs font-bold uppercase tracking-[0.08em] text-gray-500 dark:text-slate-400">
          {label}
          {required ? <span className="ml-1 text-rose-500">*</span> : null}
        </label>
      ) : null}
      {children}
      {error ? (
        <p className="mt-1.5 text-xs font-medium text-rose-600 dark:text-rose-400">{error}</p>
      ) : hint ? (
        <p className="mt-1.5 text-xs text-gray-400 dark:text-slate-500">{hint}</p>
      ) : null}
    </div>
  );
}

/** The rounded search field used above every data table. */
export function SearchInput({ className = "", ...rest }) {
  return (
    <div className={`relative ${className}`}>
      <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-500">
        <StrokeIcon className="h-4 w-4">
          <circle cx="11" cy="11" r="7" />
          <path d="m20 20-3.5-3.5" />
        </StrokeIcon>
      </span>
      <input
        type="search"
        className={`h-11 w-full rounded-full border pl-11 pr-4 text-sm ${CONTROL} ${FOCUS}`}
        {...rest}
      />
    </div>
  );
}

/**
 * Toggle switch. Green reads as "untouched / on", red as "switched for the
 * pending action" — the semantics the Automatic workspace established.
 */
export function Switch({ checked, disabled, onToggle, label, small = false }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={onToggle}
      className={`relative shrink-0 rounded-full border p-0.5 transition disabled:cursor-not-allowed disabled:opacity-50 ${
        small ? "h-5 w-9" : "h-6 w-11"
      } ${
        disabled
          ? "border-gray-300 bg-gray-200 dark:border-[#3a3a3d] dark:bg-[#2a2a2c]"
          : checked
          ? "border-emerald-500 bg-emerald-500"
          : "border-rose-500 bg-rose-500"
      }`}
    >
      <span
        className={`block rounded-full bg-white shadow-sm transition ${
          small ? "h-3.5 w-3.5" : "h-4 w-4"
        } ${checked ? (small ? "translate-x-4" : "translate-x-5") : "translate-x-0"}`}
      />
    </button>
  );
}

/**
 * Pill tabs. `items` is [{ id, label, badge? }]; the active pill carries the
 * brand fill so the current view is obvious at a glance.
 */
export function Tabs({ items, value, onChange, className = "" }) {
  return (
    <div
      className={`inline-flex flex-wrap gap-1 rounded-full border p-1 ${SUNKEN} ${className}`}
      role="tablist"
    >
      {items.map((item) => {
        const active = item.id === value;
        return (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(item.id)}
            className={`inline-flex h-9 items-center gap-2 rounded-full px-4 text-sm font-semibold transition ${
              active
                ? `${ACCENT_GRADIENT} text-white shadow-lg shadow-red-500/25`
                : "text-gray-500 hover:text-gray-800 dark:text-slate-400 dark:hover:text-slate-200"
            }`}
          >
            {item.label}
            {item.badge != null ? (
              <span
                className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold ${
                  active ? "bg-white/25 text-white" : "bg-gray-200 text-gray-600 dark:bg-[#303032] dark:text-slate-300"
                }`}
              >
                {item.badge}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Display                                                                     */
/* -------------------------------------------------------------------------- */

const BADGE_TONES = {
  neutral: "bg-gray-100 text-gray-600 dark:bg-[#262628] dark:text-slate-300",
  accent: "bg-red-500/10 text-red-600 dark:bg-orange-400/10 dark:text-orange-300",
  success: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
  danger: "bg-rose-500/15 text-rose-600 dark:text-rose-300",
  warning: "bg-amber-500/15 text-amber-700 dark:text-amber-300",
  info: "bg-sky-500/15 text-sky-700 dark:text-sky-300",
};

export function Badge({ tone = "neutral", className = "", children }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-[0.06em] ${BADGE_TONES[tone] || BADGE_TONES.neutral} ${className}`}
    >
      {children}
    </span>
  );
}

/** The brand credits pill: a dot, a caption, and the balance. */
export function CreditsPill({ label = "Credits", value, className = "" }) {
  return (
    <div className={`flex items-center gap-2 rounded-full border px-3 py-1.5 ${CONTROL} ${className}`}>
      <span className={`h-2 w-2 rounded-full ${ACCENT_GRADIENT}`} />
      <span className="text-xs font-medium text-gray-500 dark:text-slate-400">{label}</span>
      <span className={`rounded-full ${ACCENT_GRADIENT} px-2 py-0.5 text-xs font-bold text-white`}>
        {value}
      </span>
    </div>
  );
}

const STAT_TONES = {
  accent: "bg-gradient-to-br from-red-500/15 to-orange-500/15 text-red-600 dark:text-orange-400",
  success: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400",
  danger: "bg-rose-500/15 text-rose-600 dark:text-rose-400",
  info: "bg-sky-500/15 text-sky-600 dark:text-sky-400",
  warning: "bg-amber-500/15 text-amber-600 dark:text-amber-400",
};

/** A headline number with its caption and icon — the dashboards' summary row. */
export function StatTile({ label, value, icon, tone = "accent", children }) {
  return (
    <div className={`flex items-center gap-4 ${PANEL} p-5`}>
      <span
        className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-[14px] ${STAT_TONES[tone] || STAT_TONES.accent}`}
      >
        {icon}
      </span>
      <div className="min-w-0">
        <p className="truncate text-2xl font-extrabold text-gray-900 dark:text-white">{value}</p>
        <p className="truncate text-sm text-gray-500 dark:text-slate-400">{label}</p>
        {children}
      </div>
    </div>
  );
}

/** A label/value tile — file identification, account details, read-only facts. */
export function InfoTile({ label, value, accent = false, className = "" }) {
  return (
    <div
      className={`rounded-[12px] border p-3 ${
        accent ? "border-red-500/30 bg-gradient-to-br from-red-500/10 to-orange-500/10" : SUNKEN
      } ${className}`}
    >
      <GroupLabel className="tracking-[0.12em]">{label}</GroupLabel>
      <p
        className={`mt-1.5 break-words text-sm font-semibold ${
          accent ? "text-red-600 dark:text-orange-300" : "text-gray-800 dark:text-slate-200"
        }`}
        title={typeof value === "string" ? value : undefined}
      >
        {value}
      </p>
    </div>
  );
}

export function EmptyState({ title, hint, icon, className = "" }) {
  return (
    <div
      className={`flex flex-col items-center justify-center gap-1.5 rounded-[14px] border border-dashed px-4 py-8 text-center ${SUNKEN} ${className}`}
    >
      <span className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 text-gray-400 dark:bg-[#262628] dark:text-slate-500">
        {icon || (
          <StrokeIcon className="h-4 w-4">
            <path d="M14 3v5h5" />
            <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
          </StrokeIcon>
        )}
      </span>
      <p className="text-sm font-semibold text-gray-600 dark:text-slate-300">{title}</p>
      {hint ? <p className="text-xs text-gray-400 dark:text-slate-500">{hint}</p> : null}
    </div>
  );
}

const ALERT_TONES = {
  error: "border-rose-500/40 bg-rose-500/10 text-rose-700 dark:text-rose-300",
  success: "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
  warning: "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300",
  info: "border-sky-500/40 bg-sky-500/10 text-sky-700 dark:text-sky-300",
};

const ALERT_ICONS = {
  error: <path d="M12 9v4M12 17h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />,
  success: <path d="M20 6 9 17l-5-5" />,
  warning: <path d="M12 9v4M12 17h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />,
  info: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 16v-4M12 8h.01" />
    </>
  ),
};

/** A tinted strip for a message the page needs to show inline. */
export function Alert({ tone = "info", className = "", onClose, children }) {
  if (!children) return null;
  return (
    <div
      role={tone === "error" ? "alert" : "status"}
      className={`flex items-start gap-2 rounded-[14px] border px-4 py-3 text-sm ${ALERT_TONES[tone] || ALERT_TONES.info} ${className}`}
    >
      <StrokeIcon className="mt-0.5 h-5 w-5 shrink-0">{ALERT_ICONS[tone]}</StrokeIcon>
      <span className="min-w-0 flex-1 break-words">{children}</span>
      {onClose ? (
        <button
          type="button"
          onClick={onClose}
          aria-label="Dismiss"
          className="shrink-0 opacity-60 transition hover:opacity-100"
        >
          <StrokeIcon className="h-4 w-4">
            <path d="M18 6 6 18M6 6l12 12" />
          </StrokeIcon>
        </button>
      ) : null}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Tables                                                                      */
/* -------------------------------------------------------------------------- */

/** The shell every data table sits in: rounded, clipped, with its own scroll. */
export function TableShell({ className = "", maxHeight = "", children }) {
  return (
    <div className={`overflow-hidden rounded-[14px] border ${SUNKEN} ${className}`}>
      <div className={`overflow-auto ${maxHeight}`}>
        <table className="min-w-full border-separate border-spacing-0 text-sm">{children}</table>
      </div>
    </div>
  );
}

// Spelled out rather than interpolated: Tailwind only generates classes it can
// find verbatim in the source, so a `text-${align}` would never be emitted.
const TH_ALIGN = { left: "text-left", center: "text-center", right: "text-right" };

export function Th({ className = "", align = "left", children, ...rest }) {
  return (
    <th
      className={`sticky top-0 z-10 border-b border-gray-200 bg-gray-100 px-4 py-3 ${TH_ALIGN[align] || TH_ALIGN.left} text-xs font-bold uppercase tracking-[0.08em] text-gray-500 dark:border-[#2c2c2e] dark:bg-[#161617] dark:text-slate-400 ${className}`}
      {...rest}
    >
      {children}
    </th>
  );
}

/** A body row. `tone="danger"` tints it, e.g. a cancelled or failed record. */
export function Tr({ className = "", tone = "default", children, ...rest }) {
  const tones = {
    default: "hover:bg-gray-50 dark:hover:bg-[#232325]",
    danger: "bg-rose-500/5 hover:bg-rose-500/10",
    success: "bg-emerald-500/5 hover:bg-emerald-500/10",
  };
  return (
    <tr className={`transition ${tones[tone] || tones.default} ${className}`} {...rest}>
      {children}
    </tr>
  );
}

export function Td({ className = "", children, ...rest }) {
  return (
    <td
      className={`border-t border-gray-200 px-4 py-2.5 text-gray-700 dark:border-[#2c2c2e] dark:text-slate-300 ${className}`}
      {...rest}
    >
      {children}
    </td>
  );
}

/** The "nothing here" row inside a table body. */
export function TdEmpty({ colSpan, children = "Nothing to show." }) {
  return (
    <tr>
      <td
        colSpan={colSpan}
        className="border-t border-gray-200 px-4 py-10 text-center text-sm text-gray-400 dark:border-[#2c2c2e] dark:text-slate-500"
      >
        {children}
      </td>
    </tr>
  );
}

/**
 * Page numbers for a long list.
 *
 * Renders a window around the current page — first, last, current ±1, with
 * ellipses — so a hundred pages stay one row. `total` and `pageSize` are
 * optional; given both, it also says which slice is on screen.
 *
 * It renders nothing for a single page, so a caller can mount it
 * unconditionally.
 */
export function Pagination({ page, totalPages, onChange, total, pageSize, className = "" }) {
  if (!totalPages || totalPages <= 1) return null;

  const pages = [];
  for (let candidate = 1; candidate <= totalPages; candidate++) {
    const isEdge = candidate === 1 || candidate === totalPages;
    const isNear = Math.abs(candidate - page) <= 1;
    if (isEdge || isNear) {
      pages.push(candidate);
    } else if (pages[pages.length - 1] !== "…") {
      pages.push("…");
    }
  }

  const step = (delta) => onChange(Math.min(totalPages, Math.max(1, page + delta)));
  const numberClass =
    "h-9 min-w-[2.25rem] rounded-full px-3 text-sm font-bold transition disabled:cursor-not-allowed disabled:opacity-40";
  const inactive =
    "border border-gray-300 bg-white text-gray-600 hover:border-red-400/60 dark:border-[#3a3a3d] dark:bg-[#262628] dark:text-slate-300";

  const from = (page - 1) * (pageSize || 0) + 1;
  const to = Math.min(total || 0, page * (pageSize || 0));

  return (
    <div className={`flex flex-wrap items-center justify-center gap-3 ${className}`}>
      {total != null && pageSize ? (
        <p className="w-full text-center text-xs text-gray-400 dark:text-slate-500 sm:w-auto">
          Showing {from}–{to} of {total}
        </p>
      ) : null}

      <div className="flex flex-wrap items-center justify-center gap-1.5">
        <button
          type="button"
          onClick={() => step(-1)}
          disabled={page <= 1}
          aria-label="Previous page"
          className={`inline-flex h-9 w-9 items-center justify-center rounded-full ${inactive} disabled:cursor-not-allowed disabled:opacity-40`}
        >
          <StrokeIcon className="h-4 w-4">
            <path d="M15 6l-6 6 6 6" />
          </StrokeIcon>
        </button>

        {pages.map((candidate, index) =>
          candidate === "…" ? (
            <span
              key={`gap-${index}`}
              className="px-1 text-sm font-bold text-gray-400 dark:text-slate-500"
            >
              …
            </span>
          ) : (
            <button
              key={candidate}
              type="button"
              onClick={() => onChange(candidate)}
              aria-current={candidate === page ? "page" : undefined}
              className={`${numberClass} ${
                candidate === page
                  ? `${ACCENT_GRADIENT} text-white shadow-lg shadow-red-500/25`
                  : inactive
              }`}
            >
              {candidate}
            </button>
          )
        )}

        <button
          type="button"
          onClick={() => step(1)}
          disabled={page >= totalPages}
          aria-label="Next page"
          className={`inline-flex h-9 w-9 items-center justify-center rounded-full ${inactive} disabled:cursor-not-allowed disabled:opacity-40`}
        >
          <StrokeIcon className="h-4 w-4">
            <path d="M9 6l6 6-6 6" />
          </StrokeIcon>
        </button>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Overlays                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * A centred dialog. Clicking the backdrop closes it; the panel keeps the page's
 * surface treatment so a modal never looks like it came from another app.
 */
export function Modal({
  open = true,
  title,
  onClose,
  children,
  footer,
  tone = "accent",
  size = "max-w-md",
  className = "",
}) {
  if (!open) return null;
  const tones = {
    accent: "border-red-500/50",
    danger: "border-rose-500/60",
    success: "border-emerald-500/50",
    plain: "",
  };
  const titleTones = {
    accent: "text-red-600 dark:text-orange-400",
    danger: "text-rose-600 dark:text-rose-400",
    success: "text-emerald-600 dark:text-emerald-400",
    plain: "text-gray-900 dark:text-white",
  };
  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={typeof title === "string" ? title : undefined}
      onClick={onClose}
    >
      <div
        className={`w-full ${size} overflow-hidden rounded-[18px] border shadow-2xl ${tones[tone] || ""} ${PANEL} ${className}`}
        onClick={(event) => event.stopPropagation()}
      >
        {title ? (
          <div className="flex items-center gap-3 border-b border-gray-200 bg-gray-50 px-5 py-4 dark:border-[#2c2c2e] dark:bg-[#161617]">
            <h3
              className={`flex-1 text-sm font-bold uppercase tracking-[0.12em] ${titleTones[tone] || titleTones.plain}`}
            >
              {title}
            </h3>
            {onClose ? (
              <IconButton title="Close" onClick={onClose} className="h-8 w-8">
                <StrokeIcon className="h-4 w-4">
                  <path d="M18 6 6 18M6 6l12 12" />
                </StrokeIcon>
              </IconButton>
            ) : null}
          </div>
        ) : null}
        <div className="px-5 py-5 text-sm text-gray-700 dark:text-slate-300">{children}</div>
        {footer ? (
          <div className="flex flex-wrap justify-end gap-3 border-t border-gray-200 px-5 py-4 dark:border-[#2c2c2e]">
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  );
}

/** A confirm dialog — the destructive-action prompt used across the admin pages. */
export function ConfirmModal({
  open = true,
  title = "Are you sure?",
  message,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  tone = "danger",
  busy = false,
  onConfirm,
  onCancel,
}) {
  return (
    <Modal
      open={open}
      title={title}
      tone={tone}
      onClose={busy ? undefined : onCancel}
      footer={
        <>
          <Button variant="ghost" size="sm" disabled={busy} onClick={onCancel}>
            {cancelLabel}
          </Button>
          <Button
            variant={tone === "danger" ? "danger" : "primary"}
            size="sm"
            disabled={busy}
            onClick={onConfirm}
          >
            {busy ? "Working…" : confirmLabel}
          </Button>
        </>
      }
    >
      {message}
    </Modal>
  );
}

/* -------------------------------------------------------------------------- */
/* Feedback                                                                    */
/* -------------------------------------------------------------------------- */

export function Spinner({ className = "h-4 w-4" }) {
  return (
    <svg
      className={`${className} animate-spin`}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
    >
      <path d="M21 12a9 9 0 1 1-6.2-8.6" />
    </svg>
  );
}

/** The full-panel loading state a page shows while its first request is in flight. */
export function PageLoader({ label = "Loading…" }) {
  return (
    <div className={`flex flex-col items-center justify-center gap-3 ${PANEL} px-4 py-16`}>
      <Spinner className="h-7 w-7 text-red-500 dark:text-orange-400" />
      <p className="text-sm font-semibold text-gray-500 dark:text-slate-400">{label}</p>
    </div>
  );
}

/** A determinate progress bar; the fill runs the animated brand sweep. */
export function ProgressBar({ value = 0, className = "" }) {
  const pct = Math.max(0, Math.min(100, Number(value) || 0));
  return (
    <div
      className={`h-2 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-[#2a2a2c] ${className}`}
      role="progressbar"
      aria-valuenow={pct}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <div className="vdp-loading-bar h-full rounded-full transition-[width] duration-300" style={{ width: `${pct}%` }} />
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Icons                                                                       */
/* -------------------------------------------------------------------------- */

/** A 24px stroke icon wrapper — pass <path>/<circle> children. */
export function StrokeIcon({ children, className = "h-4 w-4" }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}
